import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Store, BadgeCheck, X, Search, Pencil, Trash2, Plus } from 'lucide-react';

const CATEGORY_OPTIONS = [
  { id: '', label: 'All Categories' },
  { id: 'cat_mobiles', label: 'Mobiles' },
  { id: 'cat_bikes', label: 'Bikes' },
  { id: 'cat_cars', label: 'Cars' },
  { id: 'cat_property', label: 'Property' },
];

const AdminDealersTab = () => {
  const [data, setData] = useState({ dealers: [], total_dealers: 0, total_revenue_inr: 0, total_dealer_payments: 0 });
  const [filter, setFilter] = useState({ category_id: '', q: '' });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {user_id, business_name, dealer_verified, dealer_category_id}
  const [granting, setGranting] = useState(null); // {user_id_input, business_name, dealer_category_id}

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.category_id) params.category_id = filter.category_id;
      if (filter.q) params.q = filter.q;
      const res = await adminAPI.listDealers(params);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load dealers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveEdit = async () => {
    try {
      const payload = {};
      if (editing.business_name !== undefined) payload.business_name = editing.business_name;
      if (editing.dealer_verified !== undefined) payload.dealer_verified = !!editing.dealer_verified;
      if (editing.dealer_category_id) payload.dealer_category_id = editing.dealer_category_id;
      await adminAPI.updateDealer(editing.user_id, payload);
      toast.success('Dealer updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const revoke = async (user_id) => {
    if (!window.confirm('Revoke dealer status? Their dealer badge will be hidden but existing subscriptions remain.')) return;
    try {
      await adminAPI.revokeDealer(user_id);
      toast.success('Dealer status revoked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const grantNew = async () => {
    if (!granting?.user_id?.trim() || !granting?.dealer_category_id) {
      toast.error('User ID and Dealer Category are required');
      return;
    }
    try {
      await adminAPI.grantDealer(
        granting.user_id.trim(),
        granting.dealer_category_id,
        granting.business_name || '',
        true,
      );
      toast.success('Dealer status granted');
      setGranting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div data-testid="admin-dealers-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-[#0A0B10] flex items-center gap-2">
          <Store size={20} className="text-[#00bf63]" /> Dealers Management
        </h2>
        <button
          data-testid="admin-grant-dealer-btn"
          onClick={() => setGranting({ user_id: '', business_name: '', dealer_category_id: 'cat_mobiles' })}
          className="bg-[#00bf63] text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-[#00a554] transition-colors flex items-center gap-1.5"
        >
          <Plus size={14} /> Grant Dealer
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Dealers" value={data.total_dealers} color="#00bf63" />
        <StatCard label="Total Dealer Payments" value={data.total_dealer_payments} color="#1800ad" />
        <StatCard label="Revenue (₹)" value={(data.total_revenue_inr / 100).toLocaleString()} color="#ff8500" />
        <StatCard label="Filter" value={filter.category_id ? CATEGORY_OPTIONS.find(c => c.id === filter.category_id)?.label : 'All'} color="#6B7280" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-xl px-3">
          <Search size={16} className="text-gray-400" />
          <input
            data-testid="admin-dealer-search-input"
            type="text"
            value={filter.q}
            onChange={(e) => setFilter(f => ({ ...f, q: e.target.value }))}
            placeholder="Search by name, email, phone, business name"
            className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none"
            onKeyDown={(e) => e.key === 'Enter' && load()}
          />
        </div>
        <select
          data-testid="admin-dealer-category-filter"
          value={filter.category_id}
          onChange={(e) => setFilter(f => ({ ...f, category_id: e.target.value }))}
          className="bg-gray-50 rounded-xl px-3 py-2.5 text-sm focus:outline-none border border-gray-100"
        >
          {CATEGORY_OPTIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button
          data-testid="admin-dealer-apply-filter"
          onClick={load}
          className="bg-[#1800ad] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#1200a0]"
        >
          Apply
        </button>
      </div>

      {/* Dealers list */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading...</div>
        ) : data.dealers.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No dealers found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3">Business / User</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Active Plan</th>
                  <th className="text-center px-4 py-3">Listings</th>
                  <th className="text-center px-4 py-3">Active Subs</th>
                  <th className="text-right px-4 py-3">Lifetime ₹</th>
                  <th className="text-center px-4 py-3">Verified</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.dealers.map((d) => (
                  <tr key={d.user_id} data-testid={`dealer-row-${d.user_id}`} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0A0B10]">{d.business_name || '(no business name)'}</div>
                      <div className="text-xs text-gray-500">{d.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full bg-[#00bf63]/10 text-[#00bf63] text-[10px] font-bold uppercase tracking-wider">
                        {(CATEGORY_OPTIONS.find(c => c.id === d.dealer_category_id)?.label) || d.dealer_category_id}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <div>{d.email}</div>
                      <div>{d.phone}</div>
                      {d.alternate_phone ? (
                        <div className="text-gray-500" data-testid={`dealer-alt-phone-${d.user_id}`}>
                          Alt: {d.alternate_phone}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {d.active_plan_name ? (
                        <>
                          <div className="font-semibold text-[#0A0B10]" data-testid={`dealer-plan-${d.user_id}`}>
                            {d.active_plan_name}
                          </div>
                          {d.plan_expires_at ? (
                            <div className="text-gray-500" data-testid={`dealer-expiry-${d.user_id}`}>
                              Exp: {new Date(d.plan_expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-gray-400">No active plan</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{d.listings_count}</td>
                    <td className="px-4 py-3 text-center">{d.active_dealer_subs}</td>
                    <td className="px-4 py-3 text-right font-bold">₹{(d.lifetime_revenue / 100).toLocaleString()}</td>
                    <td className="px-4 py-3 text-center">
                      {d.dealer_verified ? (
                        <BadgeCheck size={18} className="inline text-[#00bf63]" />
                      ) : (
                        <span className="text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          data-testid={`edit-dealer-${d.user_id}`}
                          onClick={() => setEditing({
                            user_id: d.user_id,
                            business_name: d.business_name || '',
                            dealer_verified: d.dealer_verified,
                            dealer_category_id: d.dealer_category_id,
                          })}
                          className="p-1.5 rounded-lg hover:bg-[#f0edff] text-[#1800ad]"
                          title="Edit dealer"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          data-testid={`revoke-dealer-${d.user_id}`}
                          onClick={() => revoke(d.user_id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                          title="Revoke dealer status"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <Modal title="Edit Dealer" onClose={() => setEditing(null)} testId="edit-dealer-modal">
          <Field label="Business Name">
            <input
              data-testid="edit-dealer-business-name"
              value={editing.business_name}
              onChange={(e) => setEditing(s => ({ ...s, business_name: e.target.value }))}
              className="w-full bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 text-sm"
              placeholder="e.g. Sharma Mobile Hub"
            />
          </Field>
          <Field label="Dealer Category">
            <select
              data-testid="edit-dealer-category"
              value={editing.dealer_category_id}
              onChange={(e) => setEditing(s => ({ ...s, dealer_category_id: e.target.value }))}
              className="w-full bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 text-sm"
            >
              {CATEGORY_OPTIONS.filter(c => c.id).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Verified">
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid="edit-dealer-verified"
                type="checkbox"
                checked={!!editing.dealer_verified}
                onChange={(e) => setEditing(s => ({ ...s, dealer_verified: e.target.checked }))}
              />
              <span>Show green Verified Dealer badge on listings</span>
            </label>
          </Field>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="flex-1 px-4 py-2.5 rounded-full border border-gray-200 text-sm font-semibold">Cancel</button>
            <button data-testid="edit-dealer-save" onClick={saveEdit} className="flex-1 px-4 py-2.5 rounded-full bg-[#1800ad] text-white text-sm font-bold hover:bg-[#1200a0]">Save</button>
          </div>
        </Modal>
      )}

      {/* Grant modal */}
      {granting && (
        <Modal title="Grant Dealer Status" onClose={() => setGranting(null)} testId="grant-dealer-modal">
          <Field label="User ID">
            <input
              data-testid="grant-dealer-user-id"
              value={granting.user_id}
              onChange={(e) => setGranting(s => ({ ...s, user_id: e.target.value }))}
              className="w-full bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 text-sm"
              placeholder="user_abc123..."
            />
          </Field>
          <Field label="Business Name (optional)">
            <input
              data-testid="grant-dealer-business-name"
              value={granting.business_name}
              onChange={(e) => setGranting(s => ({ ...s, business_name: e.target.value }))}
              className="w-full bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 text-sm"
              placeholder="e.g. Sharma Mobile Hub"
            />
          </Field>
          <Field label="Dealer Category">
            <select
              data-testid="grant-dealer-category"
              value={granting.dealer_category_id}
              onChange={(e) => setGranting(s => ({ ...s, dealer_category_id: e.target.value }))}
              className="w-full bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 text-sm"
            >
              {CATEGORY_OPTIONS.filter(c => c.id).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <p className="text-xs text-gray-500 mt-2">This only flips the dealer badge — it does NOT create a paid subscription. Use the Users tab to grant a dealer subscription separately.</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setGranting(null)} className="flex-1 px-4 py-2.5 rounded-full border border-gray-200 text-sm font-semibold">Cancel</button>
            <button data-testid="grant-dealer-save" onClick={grantNew} className="flex-1 px-4 py-2.5 rounded-full bg-[#00bf63] text-white text-sm font-bold hover:bg-[#00a554]">Grant</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{label}</div>
    <div className="text-2xl font-black" style={{ color }}>{value}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid={testId}>
    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 relative">
      <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="Close">
        <X size={18} />
      </button>
      <h3 className="text-lg font-bold mb-4">{title}</h3>
      {children}
    </div>
  </div>
);

export default AdminDealersTab;
